<template>
  <div>
    <legend class="q-mb-sm text-neutral text-weight-medium text-md">
      Association
    </legend>
    <div v-if="loading" class="q-mx-auto">
      <q-spinner class="q-mt-xl" color="primary" size="30px" />
    </div>

    <div v-else>
      <!-- Row 1: purchase + sales -->
      <div class="row q-col-gutter-md q-mb-md">
        <div v-for="sectionKey in firstRowKeys" :key="sectionKey" class="col-6">
          <span class="text-semibold q-ml-xxs">
            {{ formatSectionTitle(sectionKey) }}
          </span>

          <div class="q-mt-sm q-ml-xxs">
            <div class="row q-col-gutter-sm" :style="{ width: '350px' }">
              <div
                v-for="(entry, entryKey) in VoucherDetail[sectionKey]"
                :key="entryKey"
              >
                <PBtn
                  flat
                  dense
                  class="bg-primary-20"
                  @click="onClick(sectionKey, entryKey, entry)"
                >
                  <span class="text-sm">
                    {{ entry.label }} ({{ entry.count }})
                  </span>
                </PBtn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2: vendor_payments + customer_receivables -->
      <div class="row q-col-gutter-md">
        <div
          v-for="sectionKey in secondRowKeys"
          :key="sectionKey"
          class="col-5"
        >
          <span class="text-semibold q-ml-xxs">
            {{ formatSectionTitle(sectionKey) }}
          </span>

          <div class="q-mt-sm q-ml-xxs">
            <div class="row q-col-gutter-sm" :style="{ width: '350px' }">
              <div
                v-for="(entry, entryKey) in VoucherDetail[sectionKey]"
                :key="entryKey"
              >
                <PBtn
                  flat
                  dense
                  class="bg-primary-20"
                  @click="onClick(sectionKey, entryKey, entry)"
                >
                  <span class="text-sm">
                    {{ entry.label }} ({{ entry.count }})
                  </span>
                </PBtn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { get } from "src/api/associated-vouchers";
import VoucherDetails from "src/modules/purchase-bills/components/VoucherDetails.vue";

const props = defineProps({
  id: {
    type: String,
    default: "",
    required: false,
  },
  type: {
    type: String,
    default: "",
    required: false,
  },
});

const voucher = ref({});
const loading = ref(false);

const { toggle: toggleVoucherDetailsDrawer } = useDrawer({
  component: VoucherDetails,
});

const labelMappings = {
  purchase: "Purchases",
  sales: "Sales",
  vendor_payments: "Vendor Payments",
  customer_receivables: "Customer Receivables",
};

const formatSectionTitle = (key) => labelMappings[key];

const chipMappings = {
  purchase: {
    ewaybill: "E-Way Bill",
    cn: "Credit Notes",
    dn: "Debit Notes",
    bill: "Purchase Bills",
    inv: "Invoices",
  },
  sales: {
    po: "Purchase Orders",
    inv: "Invoices",
    cn: "Credit Notes",
    dn: "Debit Notes",
    ewaybill: "E-Way Bill",
    qc: "Quality Checks",
    pod: "Proof of Deliveries",
  },
  vendor_payments: {
    pr: "Payments",
  },
  customer_receivables: {
    payments_received: "Payments",
  },
};

const VoucherDetail = computed(() => {
  const result = {};

  for (const [sectionKey, sectionValue] of Object.entries(voucher.value)) {
    result[sectionKey] = {};

    for (const [entryKey, entryValue] of Object.entries(sectionValue)) {
      result[sectionKey][entryKey] = {
        ...entryValue,
        label:
          chipMappings?.[sectionKey]?.[entryKey] || labelMappings[entryKey],
      };
    }
  }

  return result;
});

const firstRowKeys = computed(() =>
  ["purchase", "sales"].filter((key) => VoucherDetail.value[key]),
);

const secondRowKeys = computed(() =>
  ["vendor_payments", "customer_receivables"].filter(
    (key) => VoucherDetail.value[key],
  ),
);

const onClose = () => {
  toggleVoucherDetailsDrawer({
    open: false,
    level: 0,
  });
};

const onClick = (section, key, entry) => {
  toggleVoucherDetailsDrawer({
    open: true,
    level: 0,
    props: {
      section,
      key,
      voucherDetail: entry,
      onClose,
    },
  });
};

watchEffect(async () => {
  if (props.id && props.type) {
    try {
      loading.value = true;

      const response = await get({ id: props.id, type: props.type });

      voucher.value = response?.data?.data;
    } catch (error) {
      console.error("Error fetching associated vouchers:", error);
    } finally {
      loading.value = false;
    }
  }
});
</script>
